import { CrossStorageClient } from "cross-storage";
import allowedDomains from "./domainToTenantMapping";

let ssoSessionStorage;
const arcPublishingHostname = "arcpublishing";
const cookieExpiresInDays = 14;

/*
 * This is a much simplified version of https://github.com/js-cookie/js-cookie
 * It is NOT for general purpose cookie handling.
 * It's been simplified specifically to be used by SsoSessionCookie
 */
class Cookies {
  static set(key, value, attributes = { samesite: "none" }) {
    if (typeof document === "undefined") {
      return;
    }

    attributes.path = attributes.path || "/";
    attributes.expires =
      attributes.expires &&
      new Date(Date.now() + attributes.expires * 864e5).toUTCString();

    key = encodeURIComponent(key);

    var stringifiedAttributes = "";
    for (var attributeName in attributes) {
      if (attributes[attributeName]) {
        stringifiedAttributes += "; " + attributeName;

        if (attributes[attributeName] === true) {
          continue;
        }

        stringifiedAttributes += "=" + attributes[attributeName];
      }
    }

    document.cookie = key + "=" + value + stringifiedAttributes;
  }

  static get(key) {
    if (typeof document === "undefined" || (arguments.length && !key)) {
      return null;
    }

    const cookies = document.cookie ? document.cookie.split("; ") : [];
    const jar = {};
    for (var i = 0; i < cookies.length; i++) {
      const parts = cookies[i].split("=");
      const value = parts.slice(1).join("=");

      try {
        const foundKey = decodeURIComponent(parts[0]);
        jar[foundKey] = value;

        if (key === foundKey) {
          break;
        }
      } catch (e) {
        continue;
      }
    }

    return key ? jar[key] : jar;
  }

  static remove(key, attributes = {}) {
    attributes.expires = -1;
    this.set(key, "", attributes);
  }
}

class SsoSessionCookie {
  constructor(ssoTokenName) {
    const hostname = document.location.hostname;
    const domainMatch = hostname.match(/.*\.(.+?)\.com$/);
    console.log("[MemberCenter] SsoSessionCookie constructor", {
      ssoTokenName,
      hostname,
      domainMatch
    });
    if (domainMatch) {
      const domain = domainMatch[1];
      console.log("[MemberCenter] SsoSessionCookie domain match", { domain });
      if (domain === arcPublishingHostname || allowedDomains[domain]) {
        //this.domain = `.${domain}.com`;
        this.domain = `.arcpublishing.com`;
        console.log(
          `[MemberCenter][SSO] Cookie storage for ${domain} / ${this.domain}`
        );
      } else {
        throw new Error(
          `[MemberCenter][SSO] Hostname is not in the list of allowed domains: ${hostname}`
        );
      }
    } else if (hostname === "localhost") {
      this.domain = hostname;
      console.log(`[MemberCenter][SSO] storage for ${hostname}`);
    } else {
      throw new Error(
        `[MemberCenter][SSO] Hostname is not in the list of allowed domains: ${hostname}`
      );
    }

    this.ssoTokenName = ssoTokenName;
  }

  set(name, value) {
    window.cookies = window.cookies || Cookies;
    console.log(`[MemberCenter][SSO] ssoSessionCookie.set`, {
      name,
      value,
      domain: this.domain,
      cookieExpiresInDays
    });
    Cookies.set(name, value, {
      domain: this.domain,
      expires: cookieExpiresInDays
    });
    Cookies.set(name, value, {
      domain: "membercenter-sdk-staging.cmg.com",
      expires: cookieExpiresInDays
    });
    Cookies.set(name, value, {
      domain: "cmg-cmg-tv-10010-sandbox.cdn.arcpublishing.com",
      expires: cookieExpiresInDays
    });
  }

  setToken(value) {
    this.set(this.ssoTokenName, value);
  }

  get(name) {
    const value = Cookies.get(name);
    if (value !== undefined && value !== null) {
      // This refreshes the expiry date
      this.set(name, value);
    }
    console.log(`[MemberCenter][SSO] Cookies.get`, { name, value });
    return value;
  }

  getToken() {
    const value = Cookies.get(this.ssoTokenName);
    if (value !== undefined && value !== null) {
      // This refreshes the expiry date
      this.set(this.ssoTokenName, value);
    }
    return value;
  }

  delete(name) {
    Cookies.remove(name, { domain: this.domain });
  }
}

class SsoSessionStorage {
  constructor(ssoTokenName, ssoHubOrigin) {
    console.log("[MemberCenter] SsoSessionStorage construct", {
      ssoTokenName,
      "this.crossDomainStorage": `new CrossStorageClient(${ssoHubOrigin})`
    });
    this.crossDomainStorage = new CrossStorageClient(ssoHubOrigin);
    this.ssoSessionCookie = new SsoSessionCookie(ssoTokenName);
    this.ssoTokenName = ssoTokenName;
  }

  async set(name, value) {
    console.log("[MemberCenter] ssoSessionStorage.set", {
      name,
      value
    });
    await this.crossDomainStorage
      .onConnect()
      .then(() => {
        console.log("[MemberCenter] crossDomainStorage.set", {
          name,
          value
        });
        this.crossDomainStorage.set(name, value);
      })
      .catch(err => {
        console.log(
          "[MemberCenter][SSO] Failed crossDomainStorage.set ... trying cookie instead",
          { name, err }
        );
      });
    this.ssoSessionCookie.set(name, value);
  }

  async setToken(value) {
    await this.set(this.ssoTokenName, value);
  }

  async get(name) {
    console.log("[MemberCenter] SsoSessionStorage get()", { name });
    let value = await this.crossDomainStorage
      .onConnect()
      .then(() => {
        return this.crossDomainStorage.get(name);
      })
      .catch(err => {
        console.log(
          "[MemberCenter][SSO] Failed crossDomainStorage.get ... trying cookie instead",
          { name, err }
        );
      });
    if (!value) {
      console.log(
        "[MemberCenter] SsoSessionStorage get() no value, trying cookie this.ssoSessionCookie.get(name))"
      );
      value = this.ssoSessionCookie.get(name);
    }
    return value;
  }

  async getToken() {
    return this.get(this.ssoTokenName);
  }

  async delete(name) {
    await this.crossDomainStorage
      .onConnect()
      .then(() => {
        this.crossDomainStorage.del(name);
      })
      .catch(err => {
        console.log(
          "[MemberCenter][SSO] Failed crossDomainStorage.delete ... trying cookie instead",
          { err }
        );
      });
    this.ssoSessionCookie.delete(name);
  }

  async deleteToken() {
    await this.delete(this.ssoTokenName);
  }
}

export default function getSsoSessionStorage(ssoTokenName, ssoHubOrigin) {
  if (!ssoSessionStorage) {
    ssoSessionStorage = new SsoSessionStorage(ssoTokenName, ssoHubOrigin);
  }

  return ssoSessionStorage;
}
